import { IconName } from "@fortawesome/fontawesome-svg-core";
import { MenuItems } from "Components/Core/Sidebar/SidebarStore";
import { get } from "lodash";
import { staIntegrationStore } from "Stores/StaIntegrationStore";

export interface InfoBoxSchema {
  backendName?: PROJECT_MODULES;
  iconName: IconName;
  label: string;
  link?: string;
  backendModule: PROJECT_MODULES;
  secondary?: boolean;
  hidden?: boolean;
  module?: PROJECT_MODULES;
  onClick?: () => void;
}

export interface ModuleDataUI extends Omit<API_ModuleData, "module"> {
  module?: PROJECT_MODULES;
  hidden?: boolean;
}

/**
 * Modules which not included in API_MODULEDATA_MODULE but have the own flag in API_Project
 * for example: PDL = 'pdlAccess'
 */
export type ExtraModules = "PDL";

/**
 * If module is implemented in React App - replace "backendModule" with "link" that corresponds to module route.
 * @param hidden - for rendering module link in sidebar and dashboard.
 * For example. module WEBCAM is present, but is used only on the Overview page,
 * so sidebar and dashboard links will not be rendered.
 * USAGE: to avoid a certain module from rendering - set "hidden: true" in infoBoxSchema.
 * Corresponding checks are made in Sidebar.tsx and ProjectWidget.tsx
 */
export const infoBoxSchema: InfoBoxSchema[] = [
  {
    backendModule: "DOCUMENTS_AND_PLANS",
    iconName: "file-alt",
    label: "Documents & plans",
    link: "documents",
  },
  {
    backendModule: "OX_MODULES",
    iconName: "user-hard-hat",
    label: "Construction site",
    link: "constructions",
  },
  {
    backendModule: "QA",
    iconName: "comments",
    label: "Q & A",
    link: "qa",
  },
  {
    backendModule: "VALUE_TABLE",
    label: "Objects and data",
    iconName: "shapes",
    link: "values",
  },
  {
    backendModule: "MAIL",
    iconName: "envelope",
    label: "Communication",
    link: "email",
  },
  {
    backendModule: "LOP",
    iconName: "tasks-alt",
    label: "LOPs",
    link: "project-management/lop",
    hidden: true,
  },
  {
    backendModule: "TASKS",
    iconName: "tasks",
    label: "Tasks",
    link: "tasks",
  },
  {
    backendModule: "BIM",
    iconName: "cube",
    label: "BIM",
    link: "bim",
  },
  {
    backendModule: "BIM_ISSUES",
    iconName: "exclamation-square",
    label: "BCF-Issues",
    link: "bim/bcf-issues",
    hidden: true,
  },
  {
    backendModule: "CALENDAR",
    iconName: "calendar-alt",
    label: "Calendar",
    link: "calendar",
  },
  {
    backendModule: "WORKFLOW",
    label: "Workflows",
    iconName: "code-branch",
    link: "workflows",
  },
  {
    backendModule: "INVESTIGATIONS",
    label: "Investigations",
    iconName: "puzzle-piece",
    link: "investigations",
  },
  {
    backendModule: "ORGANIZATIONS",
    iconName: "money-bill",
    label: "Bidder groups",
    hidden: true,
  },
  {
    backendModule: "TENDER_MODERN",
    iconName: "gavel",
    label: "Calls for tenders",
    link: "tenders",
  },
  {
    backendModule: "PROJECT_MANAGMENT",
    iconName: "user-chart",
    label: "Project management",
    link: "project-management",
  },
  {
    backendModule: "PROTOCOLLMANAGEMENT",
    iconName: "calendar-day",
    label: "Meetings",
    link: "project-management/meetings",
    hidden: true,
  },
  {
    backendModule: "ONLINEBAUABNAHME",
    iconName: "user-hard-hat",
    label: "Defectmanagement",
    link: null,
    hidden: true,
  },
  {
    backendModule: "STA_INTEGRATION",
    iconName: "external-link-alt",
    label: "STA",
    link: "sta",
    hidden: true,
  },
];

const infoBoxSecondarySchema: InfoBoxSchema[] = [
  {
    backendModule: "CONTACTS",
    label: "Contacts",
    iconName: "address-book",
    secondary: true,
    link: "contacts",
  },
  {
    backendModule: "GROUPS",
    label: "Groups",
    iconName: "users",
    secondary: true,
    link: "groups",
  },
  {
    backendModule: "REPORTS",
    label: "Reports",
    iconName: "chart-line",
    secondary: true,
    link: "reports",
  },
  {
    backendModule: "PROJECTSETTINGS",
    label: "Project Settings",
    iconName: "cog",
    secondary: true,
    link: "settings",
  },
];

const actionButtonSchema = [
  {
    backendModule: "MAIL",
    label: "New message",
    iconName: "envelope",
  },
  {
    backendModule: "TASKS",
    label: "New task",
    iconName: "tasks",
  },
  {
    backendModule: "WORKFLOW",
    label: "New workflow",
    iconName: "code-commit",
  },
];

// ModuleDataUI is used to track if module should be hidden
export function mapInfoBoxProps(data: ModuleDataUI[]): moduleDataItemUI[] {
  return data
    .filter(({ module }) => {
      const hasModule = !!infoBoxSchema.find((j) => j.backendModule === module);
      // if (!hasModule) {
      //   console.warn(`module "${module}" not found!`);
      // }
      return hasModule;
    })
    .map(({ module, ...rest }) => {
      const uiProps = infoBoxSchema.find((j) => j.backendModule === module);
      return {
        module,
        ...rest,
        ...uiProps,
      };
    });
}

export const mapSidebarMenus = (data: MenuItems[], projectName: string, projectId: number): MenuItems[] => {
  const newData: MenuItems[] = [];
  data.forEach(({ module, position, ...rest }) => {
    const uiProps = [...infoBoxSchema, ...infoBoxSecondarySchema].find((j) => j.backendModule === module);
    let currentLink: string = `/project/${projectId}/${uiProps?.link}`;
    let currentLabel: string = uiProps?.label;
    let currentIconName: IconName = uiProps?.iconName;
    let currentModule: PROJECT_MODULES = module;

    if (module.toLowerCase() === projectName.toLowerCase()) {
      currentLink = `/project/${projectId}/overview`;
      currentLabel = projectName;
      currentIconName = "info-circle";
      currentModule = "PROJECT_OVERVIEW";
    }

    newData[position] = {
      ...rest,
      ...uiProps,
      link: currentLink,
      label: currentLabel,
      iconName: currentIconName,
      module: currentModule,
    };

    if (module === "STA_INTEGRATION") {
      newData[position].onClick = () => staIntegrationStore.openSta(projectId);
      newData[position].hidden = false;
    }
  });

  return newData;
};

export function mapActionButtonProps(data: API_ModuleData[]): actionsItemUI[] {
  return data.map(({ module }) => {
    try {
      const { iconName, label } = actionButtonSchema.find((j) => j.backendModule === module);
      return { iconName, label };
    } catch (error) {
      // console.warn(`module "${module}" not found in actions!`);
      return null;
    }
  });
}

export function mapTypeToIconName(data: API_Notification[]): LastActionsUI[] {
  return data.map((val) => {
    const infoBox = infoBoxSchema.find((j) => j.backendModule === val.module);
    if (!infoBox) {
      console.warn(`module for "${JSON.stringify(val)}" not found!`);
      return { ...val, iconName: "empty-set" };
    }
    return {
      ...val,
      iconName: infoBox.iconName,
    } as LastActionsUI;
  });
}

/**
 * Resolves a project's `startWith` value to the corresponding project-relative route path.
 * Handles special cases where the startWith module is not a standard sidebar module
 * (e.g. DOCUMENTS, VIEW, KPI_DASHBOARD) and falls back to looking up the module link
 * in the infoBoxSchema.
 */
export const getStarterModulePath = (
  startWith: API_PROJECT_STARTWITH | "KPI_DASHBOARD",
  modules?: moduleDataItemUI[]
): string | null => {
  if (!startWith) {
    return null;
  }

  if (modules?.length > 0) {
    const matchedModule = modules.find((m) => m.module === startWith);
    if (matchedModule?.link) {
      return matchedModule.link;
    }
  }

  switch (startWith) {
    case "DOCUMENTS":
      return "documents/docs";
    case "VIEW":
      return "documents/views";
    case "KPI_DASHBOARD":
      return "overview/kpi-dashboard";
    default: {
      const schema = infoBoxSchema.find((s) => s.backendModule === startWith);
      return schema?.link ?? null;
    }
  }
};

/**
 * BIM-supported model file types.
 * Used to restrict file selection (e.g. in BCF model linking) to formats
 * that can be processed/displayed as BIM models.
 */
export const BIM_MODEL_FILE_TYPES = ["xkt", "ifc", "ifczip", "laz", "las", "cityjson", "glb", "gltf", "stl", "ply"];

/**
 * Utility function for robust sorting
 * @param param0 - object with configurations for sorting
 * @param array - array of object that should be sorted
 */
export function sortArray<T = object>(
  { namespace, order }: { namespace: string; order: "asc" | "desc" },
  array: Array<T>
) {
  return array.sort((a, b) => {
    const aProp = get(a, namespace, 0);
    const bProp = get(b, namespace, 0);
    if (order === "asc") {
      return aProp > bProp ? 1 : aProp < bProp ? -1 : 0;
    } else {
      if (aProp < bProp) {
        return 1;
      } else if (aProp > bProp) {
        return -1;
      } else {
        return 0;
      }
    }
  });
}
